import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { fireCustomLinkClickGA } from "@directv-digital/dds.services.analytics-service";
import { renderComparisonTable } from "./renderComparisonTable";
import { formatAria } from "./dtv-competitor-comparison-table-types";
import { TabTray } from "../tab-tray/tab-tray";

// Helpers to normalize differing backend feed shapes
function normalizeSection(raw: any) {
  if (!raw) {
    return null;
  }
  const featureOrder = Array.isArray(raw.featureOrder) ?
    raw.featureOrder :
    raw.featureOrder?.items || [];
  const featuresObj = raw.features?.items || raw.features || {};
  const mainPackages = raw.mainPackages || {};
  const competitorsList = raw.competitorsList || {};
  const columnWidths = raw.columnWidths || {};
  return {
    featureOrder,
    features: featuresObj,
    mainPackages,
    competitorsList,
    columnWidths
  };
}

function getItems(obj: any) {
  if (!obj) {
    return {};
  }
  return obj.items || obj;
}

export const DtvCompetitorComparisonTable = (props: any) => {
  try {
    const bodyProps = props?.competitorComparison?.competitorTableBodyProps;
    const headProps = props?.competitorTableHeadProps || props?.competitorComparison?.competitorTableHeadProps;

    if (!headProps) {
      console.warn("competitorTableHeadProps missing");
      return null;
    }
    if (!bodyProps) {
      console.warn("competitorTableBodyProps missing");
      return null;
    }

    const ariaData: any = bodyProps?.aria || {};
    const isGenrePack = !!headProps?.isGenrePack;
    const tabTrayTabs = isGenrePack ? [] : (headProps?.tabs || []);
    const [activeTab, setActiveTab] = useState(0);
    const currentTabKey = tabTrayTabs?.[activeTab]?.key;

    const bodyFeed = useMemo(() => {
      const rawSection = isGenrePack ? bodyProps?.genrePack : bodyProps?.[currentTabKey as keyof typeof bodyProps];
      return normalizeSection(rawSection) || {
        featureOrder: [],
        features: {},
        mainPackages: {},
        competitorsList: {},
        columnWidths: {}
      };
    }, [isGenrePack, bodyProps, currentTabKey]);

    // Build features array { key, label, tooltip, logo } memoized
    const features = useMemo(() => (bodyFeed?.featureOrder || []).map((key: string) => {
      const featureObj = bodyFeed?.features?.[key];
      const base = Array.isArray(featureObj) ? featureObj?.[0] : featureObj;
      return {
        key,
        label: base?.content || key,
        tooltip: base?.tooltip || "",
        logo: base?.logo || null
      };
    }), [bodyFeed]);

    // Main packages
    const mainPackages = headProps?.mainPackages || [];
    const mainPackageKeys = mainPackages?.map((p: any) => p?.key) || [];
    const defaultMainPackageKey = mainPackages?.find((p: any) => p?.default)?.key || mainPackageKeys?.[0];
    const [selectedMainPackage, setSelectedMainPackage] = useState(defaultMainPackageKey);

    // Competitors
    const getCompetitorsForTab = useCallback((tabKey: string) => {
      if (isGenrePack) {
        return headProps?.competitors || [];
      }
      if (!tabKey) {
        return [];
      }
      const key = `${tabKey}Competitors`; // e.g., cableCompetitors, streamingAppsCompetitors
      const arr = (headProps as any)?.[key];
      return Array.isArray(arr) ? arr : [];
    }, [headProps, isGenrePack]);

    const competitorsForContext = useMemo(() => getCompetitorsForTab(currentTabKey), [getCompetitorsForTab, currentTabKey]);
    const competitorKeys = useMemo(() => competitorsForContext?.map((c: any) => c?.key) || [], [competitorsForContext]);
    const defaultCompetitorKey = useMemo(() => competitorsForContext?.find((c: any) => c?.default)?.key || competitorKeys?.[0], [competitorsForContext, competitorKeys]);

    const [selectedCompetitor, setSelectedCompetitor] = useState(defaultCompetitorKey);
    const [tabCompetitorSelections, setTabCompetitorSelections] = useState<Record<string, string>>({});

    useEffect(() => {
      if (isGenrePack) {
        return;
      }
      if (!currentTabKey) {
        return;
      }
      setTabCompetitorSelections(prev => {
        const existing = prev[currentTabKey];
        if (existing && competitorKeys.includes(existing)) {
          return prev;
        }
        return {
          ...prev,
          [currentTabKey]: defaultCompetitorKey
        };
      });
    }, [isGenrePack, currentTabKey, competitorKeys, defaultCompetitorKey]);
    // Prefetch competitor logos
    useEffect(() => {
      const arr = isGenrePack ? (headProps?.competitors || []) : competitorsForContext;
      arr?.forEach((c: any) => {
        if (c?.logo) {
          const img = new Image();
          img.src = c?.logo;
        }
      });
    }, [isGenrePack, headProps?.competitors, competitorsForContext]);

    const selectRef = useRef<HTMLDivElement | null>(null);
    const selectInputRef = useRef<any>(null);
    const [menuWidth, setMenuWidth] = useState<string | number>("");
    const [selectOpen, setSelectOpen] = useState(false);
    const [mainSelectOpen, setMainSelectOpen] = useState(false);

    useEffect(() => {
      const handleResize = () => {
        if (window?.innerWidth <= 600 && selectRef?.current) {
          setMenuWidth(selectRef?.current?.offsetWidth);
        } else {
          setMenuWidth("");
        }
      };
      handleResize();
      window?.addEventListener?.("resize", handleResize);
      return () => window?.removeEventListener?.("resize", handleResize);
    }, []);

    const handleCompetitorChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e?.target?.value;
      if (isGenrePack) {
        setSelectedCompetitor(value);
      } else if (currentTabKey) {
        setTabCompetitorSelections(prev => ({
          ...prev,
          [currentTabKey]: value
        }));
      }
      setSelectOpen(false);
      setTimeout(() => {
        (document?.activeElement as HTMLElement)?.blur?.();
      }, 0);
      fireCustomLinkClickGA?.({
        event: "trackEvent",
        event_name: "custom_link_click",
        linkName: value?.toLowerCase?.(),
        linkPosition: "competitor comparison",
        linkDestination: window?.location?.href
      });
    }, [isGenrePack, currentTabKey]);

    const handleMainPackageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedMainPackage(e?.target?.value);
      setMainSelectOpen(false);
      setTimeout(() => {
        (document?.activeElement as HTMLElement)?.blur?.();
      }, 0);
      fireCustomLinkClickGA?.({
        event: "trackEvent",
        event_name: "custom_link_click",
        linkName: e?.target?.value?.toLowerCase?.(),
        linkPosition: "dtv package comparison",
        linkDestination: window?.location?.href
      });
    }, []);

    const handleTabChange = useCallback((idx: number) => {
      setActiveTab(idx);
    }, []);

    const selectedCompetitorKey = useMemo(
      () => (isGenrePack ?
        selectedCompetitor :
        (currentTabKey && tabCompetitorSelections[currentTabKey]) || defaultCompetitorKey),
      [isGenrePack, selectedCompetitor, tabCompetitorSelections, currentTabKey, defaultCompetitorKey]
    );

    // Generated by Copilot- Start - 2026-03-27 21:20:43
    // Memoize competitors map to avoid new object reference on every render
    const competitorsMap = useMemo(() => {
      const arr = isGenrePack ? headProps?.competitors : competitorsForContext;
      return (arr || []).reduce((acc: Record<string, any>, cur: any) => {
        if (cur?.key) {
          acc[cur.key] = cur;
        }
        return acc;
      }, {});
    }, [isGenrePack, headProps?.competitors, competitorsForContext]);
    // Generated by Copilot- End - 2026-03-27 21:20:43

    // SSR/client detection
    const [hydrated, setHydrated] = useState(false);
    useEffect(() => {
      setHydrated(true);
    }, []);

    return (
      <DtvBox
        style={{
          padding: 12,
          borderRadius: 12,
          maxWidth: 900,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box"
        }}
      >
        {headProps?.title && (
          <DtvText
            component="p"
            content={headProps?.title}
            variant="subhead1medium"
            style={{
 marginBottom: "16px",
textAlign: "center"
}}
          />
        )}
        <DtvBox
          style={{
            width: "100%",
            maxWidth: "100vw",
            overflowX: "hidden",
            boxSizing: "border-box"
          }}
        >
          {!isGenrePack && (tabTrayTabs?.length || 0) > 0 && (
            <DtvBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: "35px"
              }}
            >
              <TabTray
                ariaLabel="Service Type Toggle"
                defaultSelectedTab={activeTab}
                handleChange={handleTabChange}
                mode="light"
                variant="tray"
                styles={{
                  height: {
 xs: "40px",
sm: "44px",
md: "48px"
},
                  maxHeight: {
 xs: "40px",
sm: "44px",
md: "48px"
},
                  minHeight: {
 xs: "40px",
sm: "44px",
md: "48px"
},
                  borderRadius: "9999px",
                  background: "#F5F6F7",
                  padding: "2px",
                  alignItems: "center",
                  borderTop: "1px solid rgba(16, 38, 65, 0.12)"
                }}
                tabs={tabTrayTabs?.map((tab: any, idx: number) => ({
                  label: typeof tab?.label === 'string' ? tab?.label : tab?.label?.content,
                  sx: {
                    width: idx === 0 ? 90 : 180,
                    minWidth: idx === 0 ? 90 : 180,
                    maxWidth: idx === 0 ? 90 : 180,
                    flex: 'unset',
                    height: {
 xs: "38px",
sm: "44px",
md: "48px"
},
                    maxHeight: {
 xs: "38px",
sm: "44px",
md: "48px"
},
                    minHeight: {
 xs: "38px",
sm: "44px",
md: "48px"
},
                    padding: "0 !important"
                  }
                }))}
              />
            </DtvBox>
          )}
          {/* SEO: Render all table combinations as hidden tables on SSR; only active table on client */}
          {hydrated ?
            (
              renderComparisonTable({
                featureColWidth: bodyFeed?.columnWidths?.featureColWidth,
                dtvColWidth: bodyFeed?.columnWidths?.dtvColWidth,
                competitorColWidth: bodyFeed?.columnWidths?.competitorColWidth,
                mainPackageLogo: headProps?.mainPackageLogo,
                mainPackageLabel: isGenrePack ? mainPackages?.find((p: any) => p?.key === selectedMainPackage)?.label : mainPackages?.[0]?.label,
                mainPackages,
                selectedMainPackage: isGenrePack ? selectedMainPackage : mainPackages?.[0]?.key,
                tooltip: headProps?.tooltip,
                aria: ariaData,
                competitorsMap,
                competitorKeysArr: competitorKeys,
                competitorKey: selectedCompetitorKey,
                mainPkgKey: isGenrePack ? selectedMainPackage : mainPackages?.[0]?.key,
                featuresArr: features,
                mainPkgDetails: isGenrePack ? getItems(bodyFeed?.mainPackages?.[selectedMainPackage]) : getItems(bodyFeed?.mainPackages?.[mainPackages?.[0]?.key]),
                competitorDetailsObj: getItems(bodyFeed?.competitorsList?.[selectedCompetitorKey]),
                isGenrePack,
                selectOpen,
                setSelectOpen,
                mainSelectOpen,
                setMainSelectOpen,
                handleCompetitorChange,
                handleMainPackageChange,
                activeTab,
                setActiveTab,
                selectRef,
                selectInputRef,
                menuWidth
              })
            ) :
            (() => {
              const tables: any[] = [];
              // Tabs
              const tabKeys = isGenrePack ? [null] : tabTrayTabs.map((tab: any) => tab.key);
              // Main Packages
              const mainPkgKeys = mainPackages.map((p: any) => p.key);
              // Competitors per tab
              tabKeys.forEach((tabKey: string | null) => {
                const competitorsArr = isGenrePack ? (headProps?.competitors || []) : getCompetitorsForTab(tabKey);
                const competitorKeysArr = competitorsArr.map((c: any) => c.key);
                mainPkgKeys.forEach((mainPkgKey: string) => {
                  competitorKeysArr.forEach((competitorKey: string) => {
                    const rawSection = isGenrePack ? bodyProps?.genrePack : bodyProps?.[tabKey as keyof typeof bodyProps];
                    const feed = normalizeSection(rawSection) || {
 featureOrder: [],
features: {},
mainPackages: {},
competitorsList: {},
columnWidths: {}
};
                    const featuresArr = (feed?.featureOrder || []).map((key: string) => {
                      const featureObj = feed?.features?.[key];
                      const base = Array.isArray(featureObj) ? featureObj?.[0] : featureObj;
                      return {
                        key,
                        label: base?.content || key,
                        tooltip: base?.tooltip || "",
                        logo: base?.logo || null
                      };
                    });
                    const mainPkgDetails = getItems(feed?.mainPackages?.[mainPkgKey]);
                    const competitorDetailsObj = getItems(feed?.competitorsList?.[competitorKey]);
                    let { featureColWidth, dtvColWidth } = feed?.columnWidths || {};
                    const { competitorColWidth } = feed?.columnWidths || {};
                    if (headProps?.tooltip?.enabled) {
                      featureColWidth = {
 ...featureColWidth,
xs: "90px"
};
                      dtvColWidth = {
 ...dtvColWidth,
xs: "130px"
};
                    }
                    const competitorsMap = (competitorsArr || []).reduce((acc: Record<string, any>, cur: any) => {
 if (cur?.key) {
 acc[cur.key] = cur;
} return acc;
}, {});
                    const mainPkgLabel = mainPackages?.find((p: any) => p?.key === mainPkgKey)?.label;
                    tables.push(renderComparisonTable({
                      featureColWidth,
                      dtvColWidth,
                      competitorColWidth,
                      mainPackageLogo: headProps?.mainPackageLogo,
                      mainPackageLabel: mainPkgLabel,
                      mainPackages,
                      selectedMainPackage: mainPkgKey,
                      tooltip: headProps?.tooltip,
                      aria: ariaData,
                      competitorsMap,
                      competitorKeysArr,
                      competitorKey,
                      mainPkgKey,
                      featuresArr,
                      mainPkgDetails,
                      competitorDetailsObj,
                      isGenrePack,
                      tableKey: `table-${tabKey || "genre"}-${mainPkgKey}-${competitorKey}`
                    }));
                  });
                });
              });
              return tables;
            })()
          }
        </DtvBox>
        <DtvBox style={{ marginTop: 24 }}>
          {headProps?.legalText?.items?.map((line: string, idx: number) => (
            <DtvText
              content={line}
              key={idx}
              aria-label={formatAria(ariaData?.footerNote, {
 idx: idx + 1,
line
})}
              style={{
                fontSize: 13,
                color: "#888",
                marginBottom: "20px"
              }}
            />
          ))}
        </DtvBox>
      </DtvBox>
    );
  } catch (error) {
    console.error(`Error in Competitor Comparison Table >> ${error}`);
    return (
      <ErrorDisplay
        component="Competitor Comparison Table Component"
        error={error}
      />
    );
  }
};
